import { type RechartsMargin as Margin } from '@precis-digital/kurama/recharts'
import {
  CHART_CARD_AXIS_STATIC,
  CHART_CARD_CHART_MARGIN,
  CHART_CARD_CHART_VIEW_MARGIN,
  SelectedOthers
} from '../constants'
import type {
  ChartCardDefaults,
  AxisCustomDrawingProps,
  BaseElementDescription,
  ChartCardChartDefinition,
  ChartDataRow,
  ChartDataValue,
  ChartSelections,
  ChartType,
  LayoutType,
  SelectionValue,
  ViewType
} from '../types'
import type {
  ChartChoice,
  ChartChoicesMap,
  ChartDimensionAxisChoice,
  ChartDimensionValuesChoice,
  ChartDimensionValuesOfChoice,
  ChartMarginsAndAxisSpaces,
  ChartMetricChoice,
  ChartMetricOrMetricsChoice,
  ChartMetricTransposedChoice,
  ChartMetricVariantsChoice,
  ChartVariantValueChoice,
  ChartVariantValuesChoice,
  DimensionsMap,
  ElementDescription,
  ElementDescriptionDimension,
  ElementsDescriptionsMap,
  FirstOrThirdAxisDetails,
  MetricsMap,
  MetricsVariantsChoice,
  SecondAxisChoiceValuesType,
  SecondAxisChoiceValueType,
  SecondAxisDefinition,
  SecondAxisDetails
} from '../internalTypes'
import { calculateStableLeftMargin } from './chart'
import {
  assertNever,
  generateIdForSelectionOthers,
  getDimensionLabel,
  getMetricFromTransposedMetricColumn,
  getValueFromTransposedMetricColumn,
  isSelectionOthersId
} from './generics'
import {
  calculateMaxTextWidth,
  calculateSpaceFromAngledLine,
  getCanvasContext,
  getTextSize,
  rotatedBoundingBox,
  setCanvasContextFont,
  truncateTextInContext
} from './graph'

export const getAxisDescription = <DataKey extends string>(
  axis: DataKey,
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>,
  selected: SelectionValue<DataKey>
): ElementDescription => {
  if (typeof selected === 'string') {
    if (selected in elementsDescriptionsMap) {
      return elementsDescriptionsMap[selected as DataKey] as ElementDescription
    }
    return elementsDescriptionsMap[axis] as ElementDescription
  }

  for (const key of selected) {
    if (key in elementsDescriptionsMap) {
      return elementsDescriptionsMap[key] as ElementDescription
    }
    if (isSelectionOthersId(key)) {
      const metricId = getMetricFromTransposedMetricColumn(key)
      if (metricId in elementsDescriptionsMap) {
        return elementsDescriptionsMap[metricId] as ElementDescription
      }
    }
  }

  return elementsDescriptionsMap[axis] as ElementDescription
}

interface CreateSecondAxisMetricDetailsProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  choice: ChartMetricChoice<DataKey, DataRow>
  axisDefinition: SecondAxisDefinition<DataKey>
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  selections: ChartSelections<DataKey>
  result: Array<SecondAxisDetails<DataKey, DataRow>>
}

export const createSecondAxisMetricDetails = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  axisDefinition,
  choicesMap,
  dimensionsMap,
  metricsMap,
  selections,
  result
}: CreateSecondAxisMetricDetailsProps<DataKey, DataRow>): void => {
  const { variant, color, pattern } = choice

  let selectedMetric = selections[axisDefinition.axis]
  let label: string | undefined

  if (variant == null) {
    label = metricsMap[selectedMetric as DataKey]?.label
  } else {
    const { type, dimension } = choicesMap[variant] as
      | ChartVariantValueChoice<DataKey, DataRow>
      | ChartVariantValuesChoice<DataKey, DataRow>
    const { newMetrics, variantsMap } = dimensionsMap[dimension] as MetricsVariantsChoice<DataKey, DataRow>
    const newMetric = newMetrics[selectedMetric as DataKey]

    if (newMetric == null) {
      label = metricsMap[selectedMetric as DataKey]?.label
    } else {
      if (type === 'variantValue') {
        const selectedVariant = selections[variant] as string
        selectedMetric = variantsMap[selectedVariant][selectedMetric as DataKey]
      }
      label = newMetric.label
    }
  }

  result.push({
    ...axisDefinition,
    type: 'metric',
    dataKeys: [selectedMetric as DataKey],
    label: label ?? axisDefinition.axis,
    color: axisDefinition.color ?? color,
    pattern: axisDefinition.pattern ?? pattern
  })
}

interface CreateSecondAxisMetricTransposedDetailsProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  choice: ChartMetricTransposedChoice<DataKey, DataRow>
  axisDefinition: SecondAxisDefinition<DataKey>
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  selections: ChartSelections<DataKey>
  result: Array<SecondAxisDetails<DataKey, DataRow>>
}

export const createSecondAxisMetricTransposedDetails = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  axisDefinition,
  choicesMap,
  dimensionsMap,
  metricsMap,
  transposedMetricsLists,
  selections,
  result
}: CreateSecondAxisMetricTransposedDetailsProps<DataKey, DataRow>): void => {
  const { variant, transpose, color, pattern } = choice
  let selectedMetric = selections[axisDefinition.axis]
  let label: string | undefined

  if (variant == null) {
    label = metricsMap[selectedMetric as DataKey]?.label
  } else {
    const variantChoice = choicesMap[variant] as ChartVariantValueChoice<DataKey, DataRow>
    const { newMetrics, variantsMap } = dimensionsMap[variantChoice.dimension] as MetricsVariantsChoice<
      DataKey,
      DataRow
    >
    const newMetric = newMetrics[selectedMetric as DataKey]
    if (newMetric == null) {
      label = metricsMap[selectedMetric as DataKey]?.label
    } else {
      const selectedVariant = selections[choice.variant] as string
      selectedMetric = variantsMap[selectedVariant][selectedMetric as DataKey]
      label = newMetric.label
    }
  }

  const choiceTransposeValues = choicesMap[transpose] as SecondAxisChoiceValuesType<DataKey, DataRow>

  const selectedOthers = selections[generateIdForSelectionOthers(choiceTransposeValues.id)] === SelectedOthers
  const selectedValues = selections[choiceTransposeValues.id] as string[]
  const otherSelected = (selections[choiceTransposeValues.dimension] as DataKey) ?? choiceTransposeValues.dimension

  const transposedMetrics = transposedMetricsLists.get(selectedMetric as DataKey)
  let selectedMetrics: DataKey[] | undefined
  if (transposedMetrics == null) {
    selectedMetrics = [selectedMetric as DataKey]
  } else {
    selectedMetrics = Array.from(transposedMetrics).filter(
      (metric) =>
        selectedValues.includes(getValueFromTransposedMetricColumn(metric)) ||
        (selectedOthers && isSelectionOthersId(metric))
    )
  }

  result.push({
    ...axisDefinition,
    type: 'metricTransposed',
    dataKeys: selectedMetrics,
    otherSelected,
    label: label ?? axisDefinition.axis,
    color,
    pattern,
    choiceValues: choiceTransposeValues,
    selectedValues,
    selectedOthers
  })
}

interface CreateSecondAxisMetricVariantsDetailsProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  choice: ChartMetricVariantsChoice<DataKey, DataRow>
  axisDefinition: SecondAxisDefinition<DataKey>
  choicesMap: ChartChoicesMap<DataKey, DataRow>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  selections: ChartSelections<DataKey>
  result: Array<SecondAxisDetails<DataKey, DataRow>>
}

export const createSecondAxisMetricVariantsDetails = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  axisDefinition,
  choicesMap,
  dimensionsMap,
  transposedMetricsLists,
  selections,
  result
}: CreateSecondAxisMetricVariantsDetailsProps<DataKey, DataRow>): void => {
  const { variant, transpose, color, pattern } = choice

  const variantChoice = choicesMap[variant] as ChartVariantValuesChoice<DataKey, DataRow>
  const { newMetrics, variantsMap } = dimensionsMap[variantChoice.dimension] as MetricsVariantsChoice<DataKey, DataRow>

  const selectedNewMetric = selections[axisDefinition.axis] as DataKey

  const newMetric = newMetrics[selectedNewMetric] as BaseElementDescription
  const selectedVariants = selections[variant] as string[]
  const selectedRawMetrics = selectedVariants.map<DataKey>(
    (selectedVariant) => variantsMap[selectedVariant][selectedNewMetric] as DataKey
  )

  if (transpose == null) {
    result.push({
      ...axisDefinition,
      type: 'metricVariants',
      dataKeys: selectedRawMetrics,
      label: newMetric.label ?? axisDefinition.axis
    })
  } else {
    const choiceValue = choicesMap[transpose] as SecondAxisChoiceValueType<DataKey, DataRow>
    const selectedValue = selections[choiceValue.id] as string
    const otherSelected = (selections[choiceValue.dimension] as DataKey) ?? choiceValue.dimension

    const selectedMetrics: DataKey[] = []
    for (const selectedMetric of selectedRawMetrics) {
      const transposedMetrics = transposedMetricsLists.get(selectedMetric)
      if (transposedMetrics == null) {
        selectedMetrics.push(selectedMetric)
      } else {
        selectedMetrics.push(...transposedMetrics)
      }
    }

    result.push({
      ...axisDefinition,
      type: 'metricVariantsTransposed',
      dataKeys: selectedMetrics,
      otherSelected,
      label: newMetric.label ?? axisDefinition.axis,
      color,
      pattern,
      choiceValue,
      selectedValue
    })
  }
}

export const getFirstAxisLabel = (
  axisDescription: ElementDescription,
  value: ChartDataValue | null | undefined,
  dataKey?: string
): string => getDimensionLabel(axisDescription as ElementDescriptionDimension, value, dataKey)

const splitTextInLines = (text: string, maxLines: number, maxLineLength: number): string => {
  const lines: string[] = []
  let split = text.split(/\S+\s+/).length - 1
  while (split > 0) {
    const [first, rest] = text.split(new RegExp(String.raw`(?<=^(?:\S+\s+){${split - 1}}\S+)\s+`))
    if (first.length <= maxLineLength) {
      lines.push(first)
      if (maxLines - lines.length === 1) {
        if (rest.length <= maxLineLength) {
          lines.push(rest)
        } else {
          lines.push(rest.slice(0, maxLineLength - 1) + '…')
        }
        return lines.join('\n')
      }
      text = rest
      split = text.split(/\S+\s+/).length - 1
    }
    split--
  }

  lines.push(text.slice(0, maxLineLength - 1) + '…')
  return lines.join('\n')
}

const splitTextInLinesWithAngleAndContext = ({
  text,
  maxLines,
  context,
  maxTextWidth,
  firstLineOffset
}: {
  text: string
  maxLines: number
  context: CanvasRenderingContext2D
  maxTextWidth: number
  firstLineOffset: number
}): string => {
  const lines: string[] = []
  let split = text.split(/\S+\s+/).length - 1
  let lineOffset = firstLineOffset
  while (split > 0) {
    const [first, rest] = text.split(new RegExp(String.raw`(?<=^(?:\S+\s+){${split - 1}}\S+)\s+`))
    if (context.measureText(first).width + lineOffset <= maxTextWidth) {
      lines.push(first)
      lineOffset = 0
      if (maxLines - lines.length === 1) {
        const width = context.measureText(rest).width
        if (width <= maxTextWidth) {
          lines.push(rest)
        } else {
          lines.push(truncateTextInContext(rest, width, maxTextWidth, context))
        }
        return lines.join('\n')
      }
      text = rest
      split = text.split(/\S+\s+/).length - 1
    }
    split--
  }

  lines.push(truncateTextInContext(text, context.measureText(text).width, maxTextWidth - lineOffset, context))
  return lines.join('\n')
}

export const truncateTextForAngledLabels = (props: {
  text: string
  textOffset: number
  maxSpace: number | undefined
  wrapLines: number | undefined
  angle: number | undefined
  context: CanvasRenderingContext2D | null
}): string => {
  const { text, textOffset, context } = props
  const maxSpace = props.maxSpace ?? 0
  const wrapLines = props.wrapLines ?? 1
  const angle = props.angle ?? 0

  if (maxSpace <= 0) {
    return text
  }

  if (context == null) {
    if (text.length <= CHART_CARD_AXIS_STATIC.angledLabels.fallbackLength) {
      return text
    }

    if (wrapLines > 1) {
      return splitTextInLines(text, wrapLines, CHART_CARD_AXIS_STATIC.angledLabels.fallbackLength)
    }

    return text.slice(0, CHART_CARD_AXIS_STATIC.angledLabels.fallbackLength - 1) + '…'
  }

  const { width, fontBoundingBoxAscent, fontBoundingBoxDescent } = context.measureText(text)
  const lineHeight = fontBoundingBoxAscent + fontBoundingBoxDescent

  let maxTextWidth = calculateMaxTextWidth({ maxSpace, lineHeight, wrapLines: 1, angle }) - textOffset

  if (width <= maxTextWidth) return text

  if (wrapLines > 1 && text.includes(' ')) {
    maxTextWidth = calculateMaxTextWidth({ maxSpace, lineHeight, wrapLines, angle })

    return splitTextInLinesWithAngleAndContext({
      text,
      maxLines: wrapLines,
      context,
      maxTextWidth,
      firstLineOffset: textOffset
    })
  }

  return truncateTextInContext(text, width, maxTextWidth, context)
}

export const getDefaultChartMarginsAndAxisSpaces = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  layout,
  type
}: ChartCardChartDefinition<DataKey, DataRow>): ChartMarginsAndAxisSpaces => {
  if (type === 'scatter') {
    return {
      margin: { ...CHART_CARD_CHART_MARGIN },
      firstAxisSpace: CHART_CARD_AXIS_STATIC.space.xScatter,
      secondAxisSpace: CHART_CARD_AXIS_STATIC.space.yHorizontal
    }
  }
  return {
    margin: { ...CHART_CARD_CHART_MARGIN },
    firstAxisSpace:
      layout === 'horizontal'
        ? CHART_CARD_AXIS_STATIC.space.xStandardHorizontal
        : CHART_CARD_AXIS_STATIC.space.yVertical,
    secondAxisSpace:
      layout === 'horizontal'
        ? CHART_CARD_AXIS_STATIC.space.yHorizontal
        : CHART_CARD_AXIS_STATIC.space.xStandardVertical
  }
}

interface GetFirstOrThirdAxisDetailsProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>
  selections: ChartSelections<DataKey>
}

export function getFirstOrThirdAxisDetails<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  axis: DataKey,
  props: GetFirstOrThirdAxisDetailsProps<DataKey, DataRow>
): FirstOrThirdAxisDetails<DataKey, DataRow>
export function getFirstOrThirdAxisDetails<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  axis: DataKey | undefined,
  props: GetFirstOrThirdAxisDetailsProps<DataKey, DataRow>
): FirstOrThirdAxisDetails<DataKey, DataRow> | undefined
export function getFirstOrThirdAxisDetails<DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  axis: DataKey | undefined,
  props: GetFirstOrThirdAxisDetailsProps<DataKey, DataRow>
): FirstOrThirdAxisDetails<DataKey, DataRow> | undefined {
  if (axis == null) {
    return undefined
  }

  const { choicesMap, dimensionsMap, metricsMap, elementsDescriptionsMap, selections } = props
  let selected = selections[axis]

  if (axis in choicesMap) {
    const choice = choicesMap[axis] as ChartMetricChoice<DataKey, DataRow> | ChartDimensionAxisChoice<DataKey, DataRow>
    const { type } = choice
    let label: string | undefined
    let choiceValues: ChartDimensionAxisChoice<DataKey, DataRow> | undefined
    let isDate = false

    switch (type) {
      case 'metric': {
        const { variant } = choice
        if (variant == null) {
          label = metricsMap[selected as DataKey]?.label
        } else {
          const variantChoice = choicesMap[variant] as ChartVariantValueChoice<DataKey, DataRow>
          const { newMetrics, variantsMap } = dimensionsMap[variantChoice.dimension] as MetricsVariantsChoice<
            DataKey,
            DataRow
          >
          const newMetric = newMetrics[selected as DataKey]
          if (newMetric == null) {
            label = metricsMap[selected as DataKey]?.label
          } else {
            const selectedVariant = selections[variant] as string
            selected = variantsMap[selectedVariant][selected as DataKey]
            label = newMetric.label
          }
        }
        break
      }
      case 'values':
        selected = choice.dimension
        isDate = dimensionsMap[selected]?.type === 'date'
        choiceValues = choicesMap[choice.dimension] as ChartDimensionValuesChoice<DataKey, DataRow>
        break
      case 'valuesOf': {
        const { dimension } = choice
        selected = selections[dimension]
        isDate = selected != null && dimensionsMap[selected]?.type === 'date'
        choiceValues = choicesMap[dimension] as ChartDimensionValuesOfChoice<DataKey, DataRow>
        break
      }
      case 'variantValues':
        selected = choice.dimension
        isDate = dimensionsMap[selected]?.type === 'date'
        choiceValues = choicesMap[choice.dimension] as ChartVariantValuesChoice<DataKey, DataRow>
        break
      default:
        assertNever(type)
    }
    return { axis, dataKey: (selected as DataKey) ?? axis, label: label ?? axis, isDate, choiceValues }
  }
  const dataKey = (selected as DataKey) ?? axis

  return {
    axis,
    dataKey,
    label: elementsDescriptionsMap[axis]?.label ?? axis,
    isDate: dimensionsMap[dataKey]?.type === 'date'
  }
}

export const getSecondAxisDetails = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  definitions,
  choicesMap,
  dimensionsMap,
  metricsMap,
  transposedMetricsLists,
  selections
}: {
  definitions: Array<SecondAxisDefinition<DataKey>>
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  selections: ChartSelections<DataKey>
}): Array<SecondAxisDetails<DataKey, DataRow>> => {
  const result: Array<SecondAxisDetails<DataKey, DataRow>> = []

  for (const axisDefinition of definitions) {
    const { chartType, axis, axisType, customProps, axisDrawing } = axisDefinition

    const selected = selections[axis]

    if (axis in choicesMap) {
      const choice = choicesMap[axis] as ChartMetricOrMetricsChoice<DataKey, DataRow>
      const { type } = choice
      let label: string | undefined

      switch (type) {
        case 'metric':
          createSecondAxisMetricDetails<DataKey, DataRow>({
            choice,
            axisDefinition,
            choicesMap,
            dimensionsMap,
            metricsMap,
            selections,
            result
          })
          break
        case 'metricTransposed':
          createSecondAxisMetricTransposedDetails<DataKey, DataRow>({
            choice,
            axisDefinition,
            choicesMap,
            dimensionsMap,
            metricsMap,
            transposedMetricsLists,
            selections,
            result
          })
          break
        case 'metricVariants':
          createSecondAxisMetricVariantsDetails<DataKey, DataRow>({
            choice,
            axisDefinition,
            choicesMap,
            dimensionsMap,
            metricsMap,
            transposedMetricsLists,
            selections,
            result
          })
          break
        case 'metrics': {
          label = ''
          result.push({
            chartType,
            axis,
            axisType,
            type,
            dataKeys: selected as DataKey[],
            label: label ?? axis,
            color: axisDefinition.color ?? choice.color,
            pattern: axisDefinition.pattern ?? choice.pattern,
            customProps,
            axisDrawing
          })
          break
        }
        default:
          assertNever(type)
      }

      continue
    }

    if (typeof selected === 'string') {
      result.push({
        chartType,
        axis,
        axisType,
        type: 'metric',
        dataKeys: [selected as DataKey],
        label: axis,
        color: axisDefinition.color,
        pattern: axisDefinition.pattern,
        customProps,
        axisDrawing
      })
    } else {
      result.push({
        chartType,
        axis,
        axisType,
        type: 'metrics',
        dataKeys: selected as DataKey[],
        label: axis,
        color: axisDefinition.color,
        pattern: axisDefinition.pattern,
        customProps,
        axisDrawing
      })
    }
  }

  return result
}

export const getSpreadSecondAxisDetails = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  definition,
  choicesMap,
  dimensionsMap,
  metricsMap,
  transposedMetricsLists,
  selections
}: {
  definition: SecondAxisDefinition<DataKey>
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  dimensionsMap: DimensionsMap<DataKey, DataRow>
  metricsMap: MetricsMap<DataKey, DataRow>
  transposedMetricsLists: Map<DataKey, Set<DataKey>>
  selections: ChartSelections<DataKey>
}): Array<SecondAxisDetails<DataKey, DataRow>> => {
  const { dataKeys, ...rest } = getSecondAxisDetails({
    definitions: [definition],
    choicesMap,
    dimensionsMap,
    metricsMap,
    transposedMetricsLists,
    selections
  })[0]

  return dataKeys.map<SecondAxisDetails<DataKey, DataRow>>((dataKey) => ({
    ...rest,
    dataKeys: [dataKey]
  }))
}

const getFirstAxisSpace = ({
  defaults,
  viewType,
  chartType,
  axisDrawing,
  firstAxisAngledLabels,
  layout,
  context,
  labels
}: {
  defaults: ChartCardDefaults
  viewType: ViewType
  chartType: ChartType
  axisDrawing: AxisCustomDrawingProps | undefined
  layout: LayoutType
  firstAxisAngledLabels: boolean
  context: CanvasRenderingContext2D | null
  labels: string[]
}): { space: number; protrudings?: number[]; labelsFontHeight: number } => {
  const customSpace = layout === 'horizontal' ? axisDrawing?.firstAxis?.height : axisDrawing?.firstAxis?.width

  if (axisDrawing?.firstAxis?.hide === true) {
    return { space: customSpace ?? 0, labelsFontHeight: 0 }
  }

  if (context == null) {
    if (chartType === 'scatter') {
      return { space: customSpace ?? CHART_CARD_AXIS_STATIC.space.xScatter, labelsFontHeight: 20 }
    }
    if (firstAxisAngledLabels) {
      return { space: customSpace ?? CHART_CARD_AXIS_STATIC.angledLabels.fallbackSpace, labelsFontHeight: 20 }
    }
    return {
      space:
        customSpace ??
        (layout === 'horizontal'
          ? CHART_CARD_AXIS_STATIC.space.xStandardHorizontal
          : CHART_CARD_AXIS_STATIC.space.yVertical),
      labelsFontHeight: 20
    }
  }

  if (chartType === 'scatter') {
    const { width: space, height: labelsFontHeight } = getTextSize(
      setCanvasContextFont(context, defaults.fonts.axisLabels),
      [labels[0]]
    )
    return {
      space: Math.max(customSpace ?? 0, space),
      labelsFontHeight
    }
  }

  if (firstAxisAngledLabels) {
    setCanvasContextFont(context, defaults.fonts.axisTicks)

    const { width: labelsMaxWidth, height: labelsFontHeight } = getTextSize(context, labels)

    if (layout === 'vertical') {
      return { space: Math.max(customSpace ?? 0, labelsMaxWidth), labelsFontHeight }
    }

    const space = rotatedBoundingBox(
      labelsMaxWidth,
      labelsFontHeight,
      CHART_CARD_AXIS_STATIC.angledLabels.angle,
      CHART_CARD_AXIS_STATIC.angledLabels.maxSpace
    ).height

    const protrudings = labels.map((label) => {
      const { width: labelWidth } = getTextSize(context, [label])
      return rotatedBoundingBox(
        labelWidth,
        labelsFontHeight,
        CHART_CARD_AXIS_STATIC.angledLabels.angle,
        CHART_CARD_AXIS_STATIC.angledLabels.maxSpace
      ).width
    })

    return { space: Math.max(customSpace ?? 0, space), protrudings, labelsFontHeight }
  }

  const { height: labelsFontHeight } = getTextSize(setCanvasContextFont(context, defaults.fonts.axisTicks), [labels[0]])
  return {
    space: Math.max(customSpace ?? 0, labelsFontHeight),
    labelsFontHeight
  }
}

const getMargins = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  firstAxisSpace,
  firstAxisAngledLabels,
  containerWidth,
  verticalLabelOffset,
  horizontalLabelOffset,
  data
}: {
  firstAxisSpace: { space: number; protrudings?: number[]; labelsFontHeight: number }
  firstAxisAngledLabels: boolean
  containerWidth: number
  verticalLabelOffset: number
  horizontalLabelOffset: number
  data: DataRow[]
}): { margin: Margin; visibleLabels?: boolean[] } => {
  const margin = {
    ...CHART_CARD_CHART_MARGIN,
    top: CHART_CARD_CHART_MARGIN.top + verticalLabelOffset,
    bottom: CHART_CARD_CHART_MARGIN.bottom + horizontalLabelOffset
  }

  if (!firstAxisAngledLabels) {
    return { margin }
  }

  const { leftMargin: stableLeftMargin, visibleLabels } = calculateStableLeftMargin({
    containerWidth,
    baseMargin: margin.left,
    space: 0,
    otherMargin: margin.right,
    otherSpace: CHART_CARD_AXIS_STATIC.space.yHorizontal,
    numDataPoints: data.length,
    labelsSpace: calculateSpaceFromAngledLine({
      length: firstAxisSpace.labelsFontHeight * 1.5,
      angle: CHART_CARD_AXIS_STATIC.angledLabels.angle
    }).horizontal,
    labelsProjections: firstAxisSpace.protrudings
  })

  return {
    margin: {
      ...margin,
      left: stableLeftMargin
    },
    visibleLabels
  }
}

const getVerticalLabelOffset = (layout: LayoutType, axisDrawing: AxisCustomDrawingProps | undefined): number =>
  (layout === 'horizontal' && axisDrawing?.secondAxis?.hide !== true) ||
  (layout === 'vertical' && axisDrawing?.firstAxis?.hide !== true)
    ? CHART_CARD_AXIS_STATIC.space.verticalLabelOffset
    : 0

const getHorizontalLabelOffset = (
  layout: LayoutType,
  chartType: ChartType,
  axisDrawing: AxisCustomDrawingProps | undefined
): number => {
  if (chartType !== 'scatter') {
    return 0
  }
  if (layout === 'horizontal') {
    if (axisDrawing?.secondAxis?.hide === true) {
      return 0
    }
    return CHART_CARD_AXIS_STATIC.space.horizontalLabelOffset
  }
  if (axisDrawing?.firstAxis?.hide === true) {
    return 0
  }
  return CHART_CARD_AXIS_STATIC.space.horizontalLabelOffset
}

export const getChartMarginsAndAxisSpaces = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  defaults,
  viewType,
  chartType,
  axisDrawing,
  firstAxisAngledLabels,
  containerWidth,
  layout,
  firstAxisDescription,
  firstAxisDataKey,
  data
}: {
  defaults: ChartCardDefaults
  viewType: ViewType
  chartType: ChartType
  axisDrawing: AxisCustomDrawingProps | undefined
  firstAxisAngledLabels: boolean
  containerWidth: number
  layout: LayoutType
  firstAxisDescription: ElementDescription
  firstAxisDataKey: DataKey
  data: DataRow[]
}): ChartMarginsAndAxisSpaces => {
  if (viewType === 'polar') {
    return {
      margin: { ...CHART_CARD_CHART_MARGIN, ...CHART_CARD_CHART_VIEW_MARGIN[viewType] },
      firstAxisSpace: 0,
      secondAxisSpace: 0
    }
  }

  const context = getCanvasContext().context

  const verticalLabelOffset = getVerticalLabelOffset(layout, axisDrawing)
  const horizontalLabelOffset = getHorizontalLabelOffset(layout, chartType, axisDrawing)

  const labels = data.map((row) =>
    getFirstAxisLabel(firstAxisDescription, row[firstAxisDataKey] as ChartDataValue, firstAxisDataKey)
  )

  const firstAxisSpace = getFirstAxisSpace({
    defaults,
    context,
    viewType,
    chartType,
    axisDrawing,
    firstAxisAngledLabels,
    layout,
    labels
  })
  const secondAxisCustomSpace =
    layout === 'horizontal' ? axisDrawing?.secondAxis?.width : axisDrawing?.secondAxis?.height

  const { margin: chartMargin, visibleLabels } = getMargins({
    firstAxisSpace,
    firstAxisAngledLabels,
    containerWidth,
    verticalLabelOffset,
    horizontalLabelOffset,
    data
  })

  return {
    margin: chartMargin,
    firstAxisSpace: firstAxisSpace.space,
    secondAxisSpace:
      axisDrawing?.secondAxis?.hide === true
        ? secondAxisCustomSpace ?? CHART_CARD_AXIS_STATIC.space.yHorizontal
        : secondAxisCustomSpace ?? 0,
    visibleLabels
  }
}
